import { sessionFetch } from "../../../auth/session-request.js";
import {
  CSRF_HEADER_NAME,
  isStateChangingMethod,
  readCsrfToken,
} from "../lib/csrf.js";

/**
 * Thin fetch wrapper for the AIRE close API.
 *
 * Responsibilities:
 *  - Prefix the configurable API base URL (the shell's close API base).
 *  - Send the in-memory access token as a Bearer credential (never read from storage —
 *    frontend-security.md §5.1). The caller supplies it per request via `getAccessToken`. It must
 *    be the ACCESS token: the authorizer checks `aud` against `api://lio-aire` (#120).
 *  - Attach the `X-AIRE-CSRF-Token` header on state-changing calls when the backend has issued
 *    the `__Host-aire-csrf` cookie (frontend-security.md §4).
 *  - Send cookies (`credentials: "include"`) so the backend can read the `__Host-aire-refresh`
 *    cookie on the token-refresh path.
 *
 * The current backend authenticates requests only with the explicit Bearer token. Until the
 * token-exchange + refresh-cookie flow is implemented, that header is also the CSRF defense:
 * browsers cannot add it to an attacker-controlled cross-site request. Once a cookie-backed
 * session exists, this client echoes the issued CSRF cookie in the required header.
 */

export class ApiError extends Error {
  readonly status: number;
  readonly url: string;

  constructor(message: string, status: number, url: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.url = url;
  }
}

export class MutationCredentialMissingError extends Error {
  constructor(method: string, path: string) {
    super(
      `Refusing to send ${method} ${path}: neither an Authorization Bearer token nor ` +
        `${CSRF_HEADER_NAME} is available.`,
    );
    this.name = "MutationCredentialMissingError";
  }
}

export interface ApiClientOptions {
  readonly baseUrl: string;
  /** Returns the current in-memory access/ID token, or `undefined` when signed out. */
  readonly getAccessToken?: () => string | undefined;
  /** Override fetch (tests inject a stub); defaults to the global. */
  readonly fetchImpl?: typeof fetch;
  /** Override CSRF cookie read (tests inject a stub); defaults to reading `document.cookie`. */
  readonly readCsrf?: () => string | undefined;
}

export interface ApiRequestInit extends Omit<RequestInit, "body"> {
  readonly body?: unknown;
}

export interface ApiDownload {
  readonly blob: Blob;
  readonly filename: string;
  readonly contentType: string;
}

export class ApiClient {
  private readonly baseUrl: string;
  private readonly getAccessToken: () => string | undefined;
  private readonly fetchImpl: typeof fetch;
  private readonly readCsrf: () => string | undefined;

  constructor(options: ApiClientOptions) {
    this.baseUrl = options.baseUrl.replace(/\/+$/u, "");
    this.getAccessToken = options.getAccessToken ?? (() => undefined);
    this.fetchImpl = (input, init) => sessionFetch(input, init, options.fetchImpl);
    this.readCsrf = options.readCsrf ?? (() => readCsrfToken());
  }

  async request<T>(path: string, init: ApiRequestInit = {}): Promise<T> {
    const { body: rawBody, headers: initHeaders, method: initMethod, ...rest } = init;
    const method = (initMethod ?? "GET").toUpperCase();
    const headers = new Headers(initHeaders);
    headers.set("Accept", "application/json");

    const token = this.getAccessToken();
    if (token !== undefined && token !== "") {
      headers.set("Authorization", `Bearer ${token}`);
    }

    let body: BodyInit | undefined;
    if (rawBody !== undefined) {
      headers.set("Content-Type", "application/json");
      body = JSON.stringify(rawBody);
    }

    if (isStateChangingMethod(method)) {
      const csrf = this.readCsrf();
      if (csrf !== undefined && csrf !== "") {
        headers.set(CSRF_HEADER_NAME, csrf);
      } else if (token === undefined || token === "") {
        throw new MutationCredentialMissingError(method, path);
      }
    }

    const url = `${this.baseUrl}${path.startsWith("/") ? path : `/${path}`}`;
    const response = await this.fetchImpl(url, {
      ...rest,
      method,
      headers,
      credentials: "include",
      ...(body !== undefined ? { body } : {}),
    });

    if (!response.ok) {
      throw new ApiError(`Request failed: ${response.status} ${response.statusText}`, response.status, url);
    }

    if (response.status === 204) {
      return undefined as T;
    }
    return (await response.json()) as T;
  }

  get<T>(path: string, init?: ApiRequestInit): Promise<T> {
    return this.request<T>(path, { ...init, method: "GET" });
  }

  post<T>(path: string, body?: unknown, init?: ApiRequestInit): Promise<T> {
    return this.request<T>(path, { ...init, method: "POST", body });
  }

  async download(path: string, fallbackFilename: string): Promise<ApiDownload> {
    const headers = new Headers({
      Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const token = this.getAccessToken();
    if (token !== undefined && token !== "") headers.set("Authorization", `Bearer ${token}`);
    const url = `${this.baseUrl}${path.startsWith("/") ? path : `/${path}`}`;
    const response = await this.fetchImpl(url, {
      method: "GET",
      headers,
      credentials: "include",
    });
    if (!response.ok) {
      throw new ApiError(
        `Download failed: ${response.status} ${response.statusText}`,
        response.status,
        url,
      );
    }
    return {
      blob: await response.blob(),
      filename: downloadFilename(response.headers.get("content-disposition"), fallbackFilename),
      contentType: response.headers.get("content-type") ?? "application/octet-stream",
    };
  }
}

function downloadFilename(contentDisposition: string | null, fallback: string): string {
  const match = /filename="?([^";]+)"?/iu.exec(contentDisposition ?? "");
  return match?.[1] ?? fallback;
}
